import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCognitoAuth } from "@/hooks/useCognitoAuth";
import { authFetch } from "@/lib/cognito";
import { toast } from "sonner";
import { AUTH_API_ENDPOINT } from "@/lib/apiDomain";
import { detectHostTenant } from "@/lib/hostTenant";
import { identityOf, workspaceSnapshotStore } from "@/lib/perUserCaches";

export interface WorkspaceBranding {
  logo?: string;
  logoWhite?: string;
  primaryColor?: string; // HSL format e.g. "262 83% 58%"
  fontFamily?: string; // Google font name e.g. "Poppins"
}

export interface Workspace {
  /** Tenant GUID — the `/organizations` API returns this (not `id`). */
  guid?: string;
  id: string;
  companyName: string;
  country: string;
  plan?: string;
  /** Plan features + limits from the API (lib planEntitlements) — read via entitlementsOf(). */
  entitlements?: import("@/lib/plans/entitlements").Entitlements | null;
  /** Account lifecycle status from the org API (open | suspended | closed). */
  status?: string;
  role?: string;
  stripeConnectedAccountId?: string;
  defaultLanguage?: string;
  customDomain?: string;
  alias?: string;
  branding?: WorkspaceBranding;
}

interface WorkspaceContextType {
  workspace: Workspace | null;
  workspaces: Workspace[];
  isLoading: boolean;
  /** True once the first workspaces fetch has settled (success or failure). */
  hasFetched: boolean;
  error: string | null;
  hasAccess: boolean;
  /** Platform super-admin (Photohawk staff) — gates staff-only maintenance actions. */
  isSuperUser: boolean;
  /** The signed-in user's email (from GET /organizations) — for exact-identity gates. */
  userEmail: string | null;
  refetchWorkspaces: () => Promise<void>;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

/**
 * Host-keyed snapshot of a resolved workspace fetch, shared across every
 * WorkspaceProvider mounted this page load. The admin boot mounts TWO providers
 * back-to-back — WorkspaceLanding resolves the role, redirects, then
 * WorkspaceLayout mounts its own provider at the destination. Without this the
 * second provider re-fetches from scratch and re-flashes the loading skeleton.
 * With it, the second mount seeds its initial state from the first's result and
 * refreshes in the background (no isLoading flash). Short TTL so a genuine reload
 * still re-validates; keyed by host so switching tenants never reuses a stale one.
 */
interface WorkspaceSnapshot {
  workspaces: Workspace[];
  workspace: Workspace | null;
  hasAccess: boolean;
  isSuperUser: boolean;
  userEmail: string | null;
  error: string | null;
  ts: number;
}
const WS_CACHE_TTL = 30_000;
// Keyed by host AND the signed-in identity (the id token's sub): one user's snapshot — e.g. a
// super-admin's platform-wide list — is never seeded into another user's provider. The store is also
// cleared outright on any user change (lib/perUserCaches).
const wsCacheKey = (identity: string | null) =>
  `${typeof window !== "undefined" ? window.location.hostname : ""}|${identity || ""}`;
const readWsCache = (identity: string | null): WorkspaceSnapshot | null => {
  if (!identity) return null;
  const snap = workspaceSnapshotStore.get(wsCacheKey(identity)) as WorkspaceSnapshot | undefined;
  if (!snap) return null;
  if (Date.now() - snap.ts > WS_CACHE_TTL) {
    workspaceSnapshotStore.delete(wsCacheKey(identity));
    return null;
  }
  return snap;
};

/** Match a fetched workspace to the current host (alias subdomain / custom domain).
 * `activeTenantGuid` is the tenant the BACKEND resolved for this exact host — used as
 * a fallback when the client-side customDomain match misses (satellite domains, www,
 * apex-vs-subdomain, or format differences the exact-match can't cover). */
const matchWorkspaceToHost = (
  workspaces: Workspace[],
  activeTenantGuid?: string | null,
): Workspace | null => {
  const host = detectHostTenant();
  if (host.type === "alias") {
    return workspaces.find((w) => (w.alias || "").toLowerCase() === host.alias.toLowerCase()) || null;
  }
  if (host.type === "custom") {
    const byDomain = workspaces.find(
      (w) => (w.customDomain || "").toLowerCase() === host.customDomain.toLowerCase(),
    );
    if (byDomain) return byDomain;
    if (activeTenantGuid) return workspaces.find((w) => w.guid === activeTenantGuid) || null;
    return null;
  }
  return null; // app host — no tenant implied by the URL
};

export const WorkspaceProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading: authLoading, tokens } = useCognitoAuth();
  const identity = identityOf(tokens.idToken);
  // Seed from the shared host-keyed snapshot so a second provider mounted during
  // the same boot (Landing → redirect → Layout) reuses the first resolve instead
  // of re-flashing the skeleton. Null on a cold boot — the normal loading path.
  const seed = readWsCache(identity);
  const [workspaces, setWorkspaces] = useState<Workspace[]>(seed?.workspaces ?? []);
  const [workspace, setWorkspace] = useState<Workspace | null>(seed?.workspace ?? null);
  const [isLoading, setIsLoading] = useState(!seed);
  const [error, setError] = useState<string | null>(seed?.error ?? null);
  const [hasAccess, setHasAccess] = useState(seed?.hasAccess ?? false);
  const [hasFetched, setHasFetched] = useState(!!seed);
  const [isSuperUser, setIsSuperUser] = useState(seed?.isSuperUser ?? false);
  const [userEmail, setUserEmail] = useState<string | null>(seed?.userEmail ?? null);

  // `background` (seeded mounts) refreshes the snapshot without toggling isLoading,
  // so the seeded content stays on screen instead of dropping back to a skeleton.
  const fetchWorkspaces = async (background = false) => {
    if (!isAuthenticated) return;
    const fetchIdentity = identity;

    try {
      if (!background) setIsLoading(true);
      setError(null);

      // Pass the browser host so the backend resolves the active tenant for THIS
      // exact host (via readByCustomDomain — covers satellite domains / www / format
      // the client-side customDomain match can't) and returns its guid.
      //
      // activeOnly=true: this hook only needs the workspace for the current host to
      // render the admin. For super-admins the backend would otherwise scan and shape
      // EVERY tenant on each page load (the dominant load cost); activeOnly makes it
      // return just the host-resolved active workspace and skip the full scan. It's a
      // no-op for customers (they always get their small accessList). The switcher
      // fetches its own searched list separately, so losing the full array here is fine.
      const browserHost = typeof window !== "undefined" ? window.location.hostname : "";
      const hostParam = browserHost ? `?host=${encodeURIComponent(browserHost)}&activeOnly=true` : `?activeOnly=true`;
      const orgUrl = `${AUTH_API_ENDPOINT}/organizations${hostParam}`;
      // authFetch: a 401 refreshes the session once and retries before failing.
      const response = await authFetch(orgUrl, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch workspaces: ${response.status}`);
      }

      const data = await response.json();
      const fetchedWorkspaces = data?.data?.tenants || [];
      const activeTenantGuid = data?.data?.activeTenantGuid || null;
      setWorkspaces(fetchedWorkspaces);
      setIsSuperUser(data?.data?.isSuperUser === true);
      setUserEmail(data?.data?.emailAddress || null);

      // The active workspace is the one implied by the HOST (alias subdomain or
      // custom domain) — there is no tenant slug in the path anymore.
      const host = detectHostTenant();
      if (host.type === "app") {
        // No host tenant (apex / localhost): the per-tenant admin can't resolve
        // a workspace here — send the user to the chooser.
        navigate("/admin/workspaces", { replace: true });
        return;
      }

      const matched = matchWorkspaceToHost(fetchedWorkspaces, activeTenantGuid);
      const nextError = matched ? null : "You don't have access to this workspace";
      if (matched) {
        setWorkspace(matched);
        setHasAccess(true);
      } else {
        setHasAccess(false);
        setError(nextError);
      }
      // Share the resolved snapshot so the next provider mounted this boot seeds
      // from it instead of re-fetching (and re-flashing the skeleton).
      // Under the identity this fetch was made as — never another user's key.
      if (fetchIdentity) workspaceSnapshotStore.set(wsCacheKey(fetchIdentity), {
        workspaces: fetchedWorkspaces,
        workspace: matched,
        hasAccess: !!matched,
        isSuperUser: data?.data?.isSuperUser === true,
        userEmail: data?.data?.emailAddress || null,
        error: nextError,
        ts: Date.now(),
      });
    } catch (err) {
      console.error("Error fetching workspaces:", err);
      setError(err instanceof Error ? err.message : "Failed to load workspaces");
      toast.error("Failed to load workspaces");
    } finally {
      setIsLoading(false);
      setHasFetched(true);
    }
  };

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      // On a tenant host, sign in against THIS tenant's admin login (carries the
      // return path so we come back to where we were headed). Use the ROUTER
      // location (basename-stripped, e.g. "/admin"), NOT window.location.pathname
      // (which includes the "/v2" base) — AdminLogin navigates to `after` through
      // the base-aware router, so a prefixed path would double to "/v2/v2/...".
      const after = encodeURIComponent(location.pathname + location.search);
      navigate(`/admin/login?after=${after}`, { replace: true });
      return;
    }

    if (isAuthenticated) {
      // Seeded from the shared snapshot → refresh in the background (no skeleton
      // flash); cold → normal foreground load.
      fetchWorkspaces(!!seed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, authLoading]);

  return (
    <WorkspaceContext.Provider
      value={{
        workspace,
        workspaces,
        isLoading: isLoading || authLoading,
        hasFetched,
        error,
        hasAccess,
        isSuperUser,
        userEmail,
        refetchWorkspaces: fetchWorkspaces,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (context === undefined) {
    throw new Error("useWorkspace must be used within a WorkspaceProvider");
  }
  return context;
};
